
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// componentsw
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

//constants
import { PHONE_NUMBER_MASK } from '@/core/constants/masks.constants'

// realisations
import { pretensions } from '@/core/realisations/pretensions.realisation'
import { invoice_scans } from '@/core/realisations/invoice_scans.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { InfoByNumT, ReceiverT, SenderT } from '@/core/types/invoice_scans.types'
import {
    GuiltySide,
    PretensionFileT,
    PretensionsGetPretensionResponseT,
    PretensionShortT,
    PretensionT,
    PretensionTypeT,
} from '@/core/types/pretensions.types'
import { CompanyT } from '@/core/types/companies.types'

// validation
import useVuelidate from '@vuelidate/core'

// composable
import { useDebounce } from 'vue-composable'
import { useMaska } from '@/common/composable/useMaska'
import { useScroll } from '@/common/composable/useScroll'
import useAccount from '@/common/composable/useAccount'

// validation
import { integer, decimal, required, requiredIf, email } from '@vuelidate/validators'
import useNotifications from '@/common/composable/useNotifications'
import { useFormData } from '@/common/composable/useFormData'

export default defineComponent({
    props: {
        initialValue: {
            type: Object as PropType<PretensionT>,
            default: null,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-select': TheSelect,
        'the-input': TheInput,
        'file-reader': FileReader,
        cheap: Cheap,
        'the-textarea': TheTextarea,
        'button-dropdown': ButtonDropdown,
        Loader,
    },
    emits: ['refresh'],
    setup(props, { emit }) {
        const account = useAccount()
        const { unmaskNumbers } = useMaska()
        const { scrollTo } = useScroll()
        const notifications = useNotifications()
        const { getFormData } = useFormData()

        const state = reactive({
            is_contract_download_loading: false,
            invoice_number: null as string | null,
            dop_invoice_number: null as string | null,
            info: {
                invoice_number: null as string | null,
                order_id: null as number | null,
                shipment_type: null as string | null,
                direction: null as string | null,
                weight: null as number | null,
                volume_weight: null as number | null,
                places: null as number | null,
                payment_method: null as string | null,
                declared_price: null as string | null,
                cod_payment: null as string | null,
                payment_type: null as string | null,
                has_scan: null as number | null,
                has_photo: null as number | null,
                annotation: null as string | null,
                sender: null as SenderT | null,
                receiver: null as ReceiverT | null,
            } as InfoByNumT,
            receiver_name: null as string | null,
            sender_name: null as string | null,

            object: null as string | null,
            client_name: null as string | null,
            client_phone: null as string | null,
            product_description: null as string | null,
            client_email: null as string | null,
            product_amount: null as number | null,
            client_comment: null as string | null,
            legal_department_comment: null as string | null,
            files_legal_department: [] as File[],

            is_info_ready: false,
            is_loading: true,
            is_declined: false,
            // type_id: null as number | null,
            type_id: null as number | null,
            company_id: null as number | null,
            files: [] as File[],
            files_signed: [] as File[],
            file_urls: [] as PretensionFileT[],
            quality_control_file: [] as PretensionFileT[],
            final_file: [] as PretensionFileT[],
            files_employee_qc: [] as File[],
            files_employee_final: [] as File[],
            files_employee_add: [] as File[],

            pretension: {
                data: null as PretensionT | null,
            } as PretensionsGetPretensionResponseT,
            quality_control_comment: null as string | null,
            security_service_comment: null as string | null,

            compensation_id: null as number | null,
            culprit_id: null as number | null,
            reimbursed_amount: null as number | null,
            culprit_name: null as string | null,
            agreement_date: null as string | null,
            agreement_comment: null as string | null,
            is_modal_visible: false,
            is_quality_control: false,
            is_sb: false,
            is_admin: false,
            subdivision_id: null as number | null,

            product_quantity: null as number | null,
            article_number: null as string | null,
            product_name: null as string | null,

            what_received: null as string | null,
            guilty_side_id: null as number | null,
        })

        const isSpark = computed(() => state.subdivision_id === 776)
        const isTypeSix = computed(() => state.type_id === 6)

        const AddPretensionValidation = {
            what_received: {
                required: requiredIf(() => isTypeSix.value),
            },
            guilty_side_id: {
                required: requiredIf(() => props.isCreate),
            },
            //
            product_quantity: {
                required: requiredIf(() => isSpark.value && props.isCreate),
                integer,
            },
            article_number: { required: requiredIf(() => isSpark.value && props.isCreate) },
            product_name: { required: requiredIf(() => isSpark.value && props.isCreate) },
            //
            // invoice_number: {
            //     required: requiredIf(() => state.type_id !== 5 && state.subdivision_id !== 776),
            // },
            client_phone: { required },
            product_description: { required },
            client_email: { required, email },
            product_amount: { required, decimal },
            client_comment: { required },
            files: {
                required: requiredIf(() => state.type_id === 4 && props.isCreate && props.isCreate),
            },
            client_name: { required },
            type_id: { required },
            company_id: { required },
            quality_control_comment: {
                required: requiredIf(() => {
                    if (isInProgressStatusQQ.value) return true
                    return false
                }),
            },
            security_service_comment: {
                required: requiredIf(() => {
                    if (isInProgressStatusSB.value) return true
                    return false
                }),
            },
            //
            compensation_id: {
                required: requiredIf(() => {
                    if (isInProgressStatusQQ.value) return true
                    return false
                }),
            },
            reimbursed_amount: {
                required: requiredIf(() => {
                    if (isInProgressStatusQQ.value) return true
                    return false
                }),
                decimal,
            },
            culprit_id: {
                required: requiredIf(() => {
                    if (isInProgressStatusQQ.value) return true
                    return false
                }),
            },
            culprit_name: {
                required: requiredIf(() => {
                    if (isInProgressStatusQQ.value) return true
                    return false
                }),
            },
            agreement_date: {
                required: requiredIf(() => {
                    if (isInProgressStatusQQ.value) return true
                    return false
                }),
            },
            agreement_comment: {
                required: requiredIf(() => {
                    if (isInProgressStatusQQ.value) return true
                    return false
                }),
            },
            //
            subdivision_id: {
                required,
            },
        }

        const SendToApproveValidation = {
            quality_control_comment: {
                required: requiredIf(() => {
                    if (isInProgressStatusQQ.value) return true
                    return false
                }),
            },
        }

        const LegalDepartmentValidation = {
            files_legal_department: { required },
        }

        const onInvoiceNumberChange = useDebounce(() => {
            if (!state.invoice_number) {
                resetForm()
                return
            }
            loadAutoComplete()
        }, 800)

        const saveFiles = async (type: string, arr_name: File[]) => {
            const form_data = new FormData()
            form_data.append('id', state.pretension.data.id.toString())
            form_data.append('type', type)
            arr_name.forEach((file) => {
                form_data.append('files[]', file)
            })
            await pretensions.addEmployeeFiles(form_data).then(() => setPretensionInfo())
        }

        const addFiles = async () => {
            state.is_loading = true
            if (state.files_employee_add.length) {
                await saveFiles('3', state.files_employee_add)
            }
            state.files_employee_add = []
            state.is_loading = false
        }

        const returnToInProgress = async () => {
            state.is_loading = true

            await pretensions.changeStatus({
                id: state.pretension.data.id,
                status_code: 'in progress',
            })
            await setPretensionInfo()
            state.is_loading = false
        }

        const saveLegalDepartmentComment = async () => {
            legal_department_form$.value.$touch()
            if (legal_department_form$.value.$invalid) {
                scrollTo('.invalid')
                notifications.notify({
                    title: 'Пожалуйста, прикрепите файлы для ЮО!',
                    type: 'danger',
                })
                return
            }

            try {
                state.is_loading = true

                const payload = {
                    comment: state.legal_department_comment,
                    files: state.files_legal_department,
                }
                await pretensions.saveLegalDepartmentComment(
                    state.pretension.data.id,
                    getFormData(payload)
                )

                await setPretensionInfo()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка сохранения комментария!`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const addQualityControlComment = async () => {
            state_approve_form$.value.$touch()
            if (state_approve_form$.value.$invalid) {
                scrollTo('.invalid')
                return
            }

            if (!state.quality_control_comment) return

            try {
                state.is_loading = true

                await pretensions.addQualityControlComment({
                    id: state.pretension.data.id,
                    quality_control_comment: state.quality_control_comment,
                })
                if (state.files_employee_qc.length) await saveFiles('8', state.files_employee_qc)

                await setPretensionInfo()
                notifications.notify({ title: 'Комментарий сохранен!', type: 'success' })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка сохранения комментария!`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const addSBComment = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) {
                scrollTo('.invalid')
                return
            }

            if (!state.security_service_comment) return

            try {
                state.is_loading = true

                await pretensions.addSBComment({
                    id: state.pretension.data.id,
                    security_service_comment: state.security_service_comment,
                })

                await setPretensionInfo()
                notifications.notify({ title: 'Отправлено в КК!', type: 'success' })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка отправки в КК!`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const saveAgreedData = async () => {
            if (
                !state.compensation_id ||
                !state.reimbursed_amount ||
                !state.culprit_id ||
                !state.culprit_name ||
                !state.agreement_date ||
                !state.agreement_comment ||
                !state.quality_control_comment
            )
                return

            const params = {
                id: state.pretension.data.id,
                compensation_id: state.compensation_id,
                reimbursed_amount: state.reimbursed_amount,
                culprit_id: state.culprit_id,
                culprit_name: state.culprit_name,
                agreement_date: state.agreement_date,
                agreement_comment: state.agreement_comment,
                quality_control_comment: state.quality_control_comment,
            }

            await pretensions.saveAgreedData(params)
        }

        const changeStatus = async (status_code: string) => {
            if (status_code !== 'in_progress_ld') {
                state_form$.value.$touch()
                if (state_form$.value.$invalid) {
                    scrollTo('.invalid')
                    return
                }
            }

            try {
                state.is_loading = true

                if (status_code === 'оn approval') {
                    await saveAgreedData()
                }

                // if (
                //     status_code === 'closed' &&
                //     ((isOnApprovalStatus.value && isDeclined.value) || isOnApprovedStatus.value)
                // ) {
                //     await saveAgreedData()
                // }

                await pretensions.changeStatus({ id: state.pretension.data.id, status_code })
                await setPretensionInfo()

                notifications.notify({ title: 'Статус изменен', type: 'success' })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка смены статуса!`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const onDeletePretension = async () => {
            const answer = confirm('Вы действительно хотите удалить претензию?')
            if (!answer) return

            await pretensions.deletePretension(state.pretension.data.id)
            emit('refresh')
        }

        const state_form$ = useVuelidate(AddPretensionValidation, state)
        const state_approve_form$ = useVuelidate(SendToApproveValidation, state)
        const legal_department_form$ = useVuelidate(LegalDepartmentValidation, state)

        const data_state = reactive({
            types: new DataState<PretensionTypeT>({}),
            compensations: new DataState<PretensionTypeT>({}),
            culprits: new DataState<PretensionTypeT>({}),
            subdivisions: new DataState<any>({}),
            companies: new DataState<CompanyT>({}),
            guilty_sides: new DataState<GuiltySide>({}),
        })

        const loadCompanies = async (page: number) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const params = { page, statusId: 2, isExtended: 0, getAll: 1 }
                const response = await companies.getList(params)
                data_state.companies.list = response.data
                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadSubdivisions = async () => {
            try {
                data_state.subdivisions.is_loading = true
                const response = await companies.getSubdivisions({ page: 1 })
                data_state.subdivisions.list = response.data

                data_state.subdivisions.is_failed = false
                data_state.subdivisions.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.subdivisions.failure.catchError(error)

                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_failed = true
                }
            } finally {
                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_loading = false
                }
            }
        }

        const loadAutoComplete = async () => {
            try {
                state.is_loading = true

                if (!state.invoice_number) return

                const response = await invoice_scans.getInfoByNum(state.invoice_number)
                state.info = response.data

                state.receiver_name = response.data.receiver.title
                state.sender_name = response.data.sender.title

                state.company_id = response.data.company_id

                state.is_info_ready = true
            } catch (error) {
                console.log(error)
            } finally {
                state.is_loading = false
            }
        }

        const loadGuiltySides = async () => {
            data_state.guilty_sides.pagination.current_page = 1

            try {
                data_state.guilty_sides.is_loading = true

                const response = await pretensions.getGuiltySides()
                data_state.guilty_sides.list = response.data
                // data_state.guilty_sides.pagination.total_pages = response.meta.last_page

                data_state.guilty_sides.is_failed = false
                data_state.guilty_sides.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.guilty_sides.failure.catchError(error)

                if (!data_state.guilty_sides.failure.error_state.is_cancel) {
                    data_state.guilty_sides.is_failed = true
                }
            } finally {
                if (!data_state.guilty_sides.failure.error_state.is_cancel) {
                    data_state.guilty_sides.is_loading = false
                }
            }
        }

        const loadTypes = async (page: number) => {
            data_state.types.pagination.current_page = page

            try {
                data_state.types.is_loading = true

                const response = await pretensions.getTypes(page)
                data_state.types.list = response.data
                data_state.types.pagination.total_pages = response.meta.last_page

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        const loadMoreTypes = async (page: number) => {
            if (page > data_state.types.pagination.total_pages) return
            data_state.types.pagination.current_page = page

            try {
                data_state.types.is_loading = true

                const response = await pretensions.getTypes(page)
                data_state.types.list = data_state.types.list.concat(response.data)

                data_state.types.pagination.total_pages = response.meta.last_page

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        const emitCancel = () => {
            emit('refresh')
        }

        const setPretensionInfo = async () => {
            try {
                state.is_loading = true

                const response = await pretensions.getPretension(props.initialValue.id)

                state.pretension = response
                state.client_name = response.data.client_name
                state.object = response.data.object
                state.client_phone = response.data.client_phone
                state.dop_invoice_number = response.data.dop_invoice_number
                state.product_description = response.data.product_description
                state.client_email = response.data.client_email
                state.type_id = response.data.type_id
                state.product_amount = response.data.product_amount
                state.client_comment = response.data.client_comment
                state.file_urls = response.data.files.data
                state.quality_control_file = response.data.quality_control_file.data
                state.final_file = response.data.final_file.data
                state.quality_control_comment = response.data.quality_control_comment
                state.security_service_comment = response.data.security_service_comment
                state.security_service_comment = response.data.security_service_comment
                state.compensation_id = response.data.compensation_id
                state.reimbursed_amount = response.data.reimbursed_amount
                state.culprit_id = response.data.culprit_id
                state.culprit_name = response.data.culprit_name
                state.agreement_date = response.data.agreement_date
                state.agreement_comment = response.data.agreement_comment
                state.company_id = response.data.company_id
                state.legal_department_comment = response.data.legal_department_comment
                state.product_name = response.data.product_name
                state.product_quantity = response.data.product_quantity
                state.article_number = response.data.article_number
                state.what_received = response.data.what_received

                if (response.data.guilty_side) {
                    state.guilty_side_id = response.data.guilty_side.id
                }

                if (response.data.subdivision) {
                    state.subdivision_id = response.data.subdivision.id
                }

                state_form$.value.$reset()
            } catch (e) {
                console.log(e)
            } finally {
                state.is_loading = false
            }
        }

        const createDocumentFromPretension = () => {
            window.print()
        }

        const updatePretensionInfo = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) {
                scrollTo('.invalid')
                return
            }

            if (
                !state.client_name ||
                !state.client_phone ||
                !state.product_description ||
                !state.product_amount ||
                !state.client_comment ||
                !state.type_id
            )
                return

            let params: PretensionShortT = {
                id: state.pretension.data.id,
                dop_invoice_number: state.dop_invoice_number,
                client_name: state.client_name,
                client_phone: unmaskNumbers(state.client_phone),
                product_description: state.product_description,
                client_email: state.client_email as string,
                product_amount: state.product_amount,
                client_comment: state.client_comment,
                type_id: state.type_id,
                subdivision_id: state.subdivision_id,
                company_id: state.company_id,
                //
                what_received: state.what_received,
                guilty_side_id: state.guilty_side_id,
                //
                product_quantity: state.product_quantity,
                article_number: state.article_number,
                product_name: state.product_name,
                object: state.object,
            }

            if (state.invoice_number) {
                params.invoice_number = state.invoice_number
            }

            try {
                state.is_loading = true
                await pretensions.updatePretensionInfo(params as PretensionShortT)
                await saveAgreedData()
                await addFiles()

                notifications.notify({ title: 'Претензия отредактирована!', type: 'success' })

                emit('refresh')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка редактирования претензии`,
                })
            } finally {
                state.is_loading = false
            }
        }

        // const uploadDocument = async () => {
        //     state.is_loading = true
        //     const form_data = new FormData()

        //     form_data.append('id', state.pretension.data.id.toString())
        //     form_data.append('file', state.files_signed[0])

        //     try {
        //         await pretensions.attachDocument(form_data)
        //     } catch (e) {
        //         console.log(e)
        //     } finally {
        //         state.is_modal_visible = false
        //         await setPretensionInfo()
        //         state.is_loading = false
        //     }
        // }

        const downloadPretension = async (download_url: string) => {
            const link = document.createElement('a')
            link.href = download_url
            link.target = '_blank'
            document.body.appendChild(link)
            link.click()
            link.remove()
        }

        const deleteFile = async (id: number) => {
            const answer = confirm('Вы действительно хотите удалить претензию?')
            if (!answer) return

            state.is_loading = true
            await pretensions.deleteFile(id)
            await setPretensionInfo()
            state.is_loading = false
        }

        const isPDF = (url: string) => {
            let extension_tmp = url.split('.')
            return extension_tmp[extension_tmp.length - 1] === 'pdf'
        }

        const sendFinalFile = async () => {
            scrollTo('.invalid')

            if (state.files_employee_final.length) {
                try {
                    state.is_loading = true

                    await saveFiles('9', state.files_employee_final)
                    notifications.notify({ title: 'Файл отправлен!', type: 'success' })

                    await changeStatus('closed')

                    emit('refresh')
                } catch (error) {
                    notifications.notifyError({
                        error,
                        title: `Ошибка отправки файла`,
                    })
                } finally {
                    state.is_loading = false
                }
            }
        }

        onBeforeMount(async () => {
            state.is_quality_control = account.havePermissions([
                'cabinet-pretension-save-quality-control-comment',
                'all-root-permission',
            ])

            state.is_sb = account.havePermissions([
                'cabinet-pretension-save-security-service-comment',
            ])

            state.is_admin = account.haveRole(11)

            loadGuiltySides()
            loadTypes(1)
            loadCompanies(1)
            loadSubdivisions()

            if (props.isUpdate && props.initialValue) {
                const responseCompensations = await pretensions.getCompensations(1)
                data_state.compensations.list = responseCompensations.data

                const responseCulprits = await pretensions.getCulprits(1)
                data_state.culprits.list = responseCulprits.data

                state.invoice_number = props.initialValue.client_title
                await loadAutoComplete()
                await setPretensionInfo()
            }

            state.is_loading = false
        })

        const isCreator = computed(() => {
            return state.pretension.data
                ? account.state.user_id === state.pretension.data.user_id
                : false
        })

        const isCreatedStatus = computed(() => {
            return state.pretension.data ? state.pretension.data.status_name === 'Создан' : false
        })

        const isUpdatableStatus = computed(() => {
            return isCreatedStatus.value || isInProgressStatusQQ.value || isInProgressStatusSB.value || isInProgressStatusJUR.value
        })

        const isInProgressStatusQQ = computed(() => {
            return state.pretension.data
                ? state.pretension.data.status_name === 'В работе у КК'
                : false
        })

        const isInProgressStatusSB = computed(() => {
            return state.pretension.data
                ? state.pretension.data.status_name === 'В работе у СБ'
                : false
        })

        const isInProgressStatusJUR = computed(() => {
            return state.pretension.data
                ? state.pretension.data.status_name === 'В работе у ЮР'
                : false
        })

        const isLegalDepartment = computed(() => {
            return state.pretension.data ? state.pretension.data.status_id === 9 : false
        })

        const isOnApprovalStatus = computed(() => {
            return state.pretension.data
                ? state.pretension.data.status_name === 'На согласовании'
                : false
        })

        const isOnApprovedStatus = computed(() => {
            return state.pretension.data
                ? state.pretension.data.status_name === 'Согласован'
                : false
        })

        const isOnAgreedStatus = computed(() => {
            return state.pretension.data
                ? state.pretension.data.status_name === 'Согласован'
                : false
        })

        const isOnClosedStatus = computed(() => {
            return state.pretension.data ? state.pretension.data.status_name === 'Закрыт' : false
        })

        const isDeclined = computed(() => {
            return state.compensation_id === 5 || state.compensation_id === 3
        })

        const addPretension = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) {
                scrollTo('.invalid')
                return
            }

            if (
                !state.client_phone ||
                !state.product_description ||
                !state.product_amount ||
                !state.client_comment ||
                !state.type_id
            )
                return

            try {
                state.is_loading = true
                const form_data = new FormData()

                if (state.type_id !== 5 && state.subdivision_id !== 776) {
                    if (state.invoice_number) {
                        form_data.append('invoice_number', state.invoice_number as string)
                    }
                } else {
                    form_data.append('dop_invoice_number', state.dop_invoice_number as string)
                }

                form_data.append(
                    'subdivision_id',
                    state.subdivision_id ? state.subdivision_id.toString() : ''
                )
                state.client_name && form_data.append('client_name', state.client_name)
                form_data.append('client_phone', unmaskNumbers(state.client_phone))
                form_data.append('product_description', state.product_description)
                if (state.client_email) form_data.append('client_email', state.client_email)
                form_data.append('product_amount', state.product_amount.toString())
                form_data.append('client_comment', state.client_comment)
                form_data.append('type_id', state.type_id.toString())

                if (state.company_id) {
                    form_data.append('company_id', state.company_id.toString())
                }

                state.files.forEach((file) => {
                    form_data.append('files[]', file)
                })

                if (isTypeSix.value) {
                    form_data.append('what_received', state.what_received as string)
                }

                if (state.guilty_side_id) {
                    form_data.append('guilty_side_id', state.guilty_side_id.toString())
                }

                if (isSpark.value) {
                    form_data.append('product_quantity', Number(state.product_quantity).toString())
                    form_data.append('article_number', state.article_number as string)
                    form_data.append('product_name', state.product_name as string)
                }

                if (!props.initialValue) {
                    form_data.append('source_id', '1')
                }

                await pretensions.createPretension(form_data)

                notifications.notify({ title: 'Претензия создана!', type: 'success' })
                emit('refresh')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Ошибка создания претензии`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const onSubdivisionChange = (value: number) => {
            if (value === 776) {
                state.product_quantity = null
                state.article_number = null
                state.product_name = null
            }
        }

        const onTypeChange = (value: number) => {
            if (value === 6) {
                state.product_quantity = null
                state.article_number = null
                state.product_name = null
            }
        }

        const resetForm = () => {
            state.type_id = null
            state.company_id = null
            state.files = []

            state.sender_name = null
            state.receiver_name = null
            state.info = {} as InfoByNumT
            state.invoice_number = null

            state.what_received = null
            state.guilty_side_id = null

            state.product_quantity = null
            state.article_number = null
            state.product_name = null

            state.is_info_ready = false

            state_form$.value.$reset()
        }

        const onReturnToInProgress = computed(() => {
            return (
                props.isUpdate &&
                (state.is_admin || state.is_quality_control) &&
                !isUpdatableStatus.value
            )
        })

        const isOtherType = computed(() => {
            if (state.type_id && state.type_id === 5) return true
            return false
        })

        const isFinalFile = computed(() => {
            return (
                isOnApprovalStatus.value &&
                !(state.final_file.length > 0) &&
                account.havePermissions(['cabinet-pretension-save-files'])
            )
        })

        const getTitle = computed(() => {
            return props.initialValue ? 'Редактирование претензии' : 'Добавление претензии'
        })

        return {
            state,
            state_form$,
            state_approve_form$,
            legal_department_form$,
            data_state,
            //
            deleteFile,
            saveFiles,
            createDocumentFromPretension,
            // uploadDocument,
            downloadPretension,
            isPDF,
            sendFinalFile,
            addFiles,
            emitCancel,
            loadAutoComplete,
            loadCompanies,
            loadTypes,
            loadMoreTypes,
            addPretension,
            resetForm,
            updatePretensionInfo,
            onInvoiceNumberChange,
            changeStatus,
            onDeletePretension,
            PHONE_NUMBER_MASK,
            returnToInProgress,
            saveLegalDepartmentComment,
            loadSubdivisions,
            addQualityControlComment,
            addSBComment,
            onSubdivisionChange,
            onTypeChange,
            loadGuiltySides,
            // computed
            isCreator,
            isCreatedStatus,
            isInProgressStatusQQ,
            isInProgressStatusSB,
            isOnApprovalStatus,
            isOnApprovedStatus,
            isOtherType,
            onReturnToInProgress,
            isOnAgreedStatus,
            isOnClosedStatus,
            isDeclined,
            isFinalFile,
            getTitle,
            isLegalDepartment,
            isSpark,
            isTypeSix,
            isUpdatableStatus,
        }
    },
})
